import type { JSX } from 'react';
import { styled } from 'styled-components';
import { Item as RadioGroupItem } from '@radix-ui/react-radio-group';
import { formatHex } from 'culori/fn';
import { ColorSwatch } from './ColorSwatch';
import { useColorPickerState } from './ColorPickerContext';
import { Tooltip } from '../Tooltip';
import type { ColorPickerOption } from './types';

const Container = styled(RadioGroupItem)`
  border: none;
  appearance: none;
  outline: none;
  align-items: center;
  border-radius: var(--wui-border-radius-rounded);
  padding: 0;
  cursor: pointer;
  user-select: none;
`;

export type ColorGridOptionProps = ColorPickerOption;

/**
 * A single selectable color swatch for use inside a `ColorGrid`. Renders as
 * a radio item with a tooltip showing the color's name or hex code.
 */
export const ColorGridOption = ({ value: rawValue, name }: ColorGridOptionProps): JSX.Element => {
  const value = rawValue.toLocaleLowerCase();
  const { valueAsHsv, nonDerivedValueAsHex } = useColorPickerState();
  // It's important that we use the internal valueAsHsv here rather than using
  // valueAsHex (which is the value passed into the ColorPicker as a prop), to
  // avoid a small timing issue: If we used valueAsHex from useColorPickerState,
  // derivativeIsSelected would be momentarily true here when it shouldn't be,
  // i.e. before the outer component renders to update the prop value after the
  // valueAsHsv has updated internally.
  const valueAsHex = formatHex(valueAsHsv);
  const isSelected = valueAsHex === value;
  const derivativeIsSelected = !isSelected && nonDerivedValueAsHex === value;

  return (
    <Tooltip
      content={
        name ?? <span style={{ fontFamily: 'var(--wui-typography-family-mono)' }}>{value}</span>
      }
    >
      <Container
        aria-label={value}
        aria-selected={valueAsHex === value}
        role="option"
        tabIndex={-1}
        value={value}
      >
        <ColorSwatch
          derivativeIsSelected={derivativeIsSelected}
          isSelected={isSelected}
          value={value}
        />
      </Container>
    </Tooltip>
  );
};

ColorGridOption.displayName = 'ColorGridOption';
